import {
  type CreateParams,
  type CreateResult,
  GetListParams,
  GetListResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
} from "react-admin";
import {
  baseUrl as serverBaseUrl,
  CreateEmployeeDtoSchema,
  CreateInstructorDtoSchema,
  EmployeeDto,
  getAuthenticatedServerClient,
  schemas,
  serverAPI,
  setUserDisabled,
} from "../api/server.api";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { applyFilter, applyPagination, applySort, areSetsEqual } from "../backoffice.utils";
import { getDefaultUserGrantToggles } from "../backoffice.access_control";
import { drivingSchoolsProvider } from "./drivingSchoolsProvider";
import { setUserRoles } from "../api/backoffice.api";
import { doc, DocumentSnapshot, onSnapshot } from "firebase/firestore";
import { firestore } from "../firebase";
import { grantsToArray, instructorsProvider } from "./instructorsProvider";
import { updateApplied } from "../utils/updateApplied";

export interface Employee {
  id: string;
  drivingSchoolId?: string;
  name: string;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  avatarUrl: string;
  disabled: boolean;
  roles: Array<string>;
  entitledForDrivingSchools?: Array<string>;
  grants: {
    [key: string]: boolean;
  };
  _sortOrder: string;
}

export const drivingSchoolEmployeeRoles = ["drivingSchoolCustomerService", "drivingSchoolAccountant"];

class EmployeesProvider extends AbstractDataProvider<Employee> {
  async getOne(_resource: string, { id }: GetOneParams): Promise<GetOneResult<Employee>> {
    const employee = await serverAPI.getEmployee(id);
    return { data: _dtoToRecord(employee) };
  }

  async getList(_resource: string, params: GetListParams): Promise<GetListResult<Employee>> {
    const { filter, sort, pagination } = params;
    const { drivingSchoolId, ...clientSideFilter } = filter;
    if (!drivingSchoolId) {
      throw new Error(`Unexpected filter: ${JSON.stringify(filter)} -- expected: {"drivingSchoolId": ...}`);
    }
    const dtos = await serverAPI.getDrivingSchoolEmployees(drivingSchoolId);
    const records = dtos.map((dto) => {
      const record = _dtoToRecord(dto);
      record.drivingSchoolId = drivingSchoolId;
      return record;
    });
    return applyPagination(applySort(applyFilter(records, clientSideFilter), sort), pagination);
  }

  async create(resource: string, { data }: CreateParams): Promise<CreateResult<Employee>> {
    const { role } = data;
    if (role === "instructor") {
      const serverClient = await getAuthenticatedServerClient();
      const dto = CreateInstructorDtoSchema.parse(data);
      const instructor = await serverClient.createInstructor(dto);
      const instructorReceived = new Promise<void>((resolve) => {
        const unsubscribe = instructorsProvider.onCreate(instructor, (instructor) => {
          if (
            instructor.firstName === dto.firstName &&
            instructor.lastName === dto.lastName &&
            areSetsEqual(new Set(dto.drivingLicenseClasses), new Set(instructor.instructing.drivingLicenseClasses))
          ) {
            unsubscribe();
            resolve();
          }
        });
      });
      await instructorReceived;
      return this.getOne(resource, { id: instructor.id });
    } else if (drivingSchoolEmployeeRoles.includes(role)) {
      const serverClient = await getAuthenticatedServerClient();
      const createEmployeeDto = CreateEmployeeDtoSchema.parse(data);
      const employeeDto = await serverClient.createEmployee(createEmployeeDto);
      return { data: _dtoToRecord(employeeDto) };
    } else {
      throw new Error(`Unsupported role: ${role}`);
    }
  }

  async update(resource: string, { id, data, previousData }: UpdateParams<Employee>): Promise<UpdateResult<Employee>> {
    const { grants, roles, entitledForDrivingSchools, firstName, lastName, phoneNumber } = data;
    if (typeof data.disabled === "boolean" && data.disabled !== previousData.disabled) {
      await setUserDisabled(id.toString(), data.disabled);
    }
    if (grants) {
      const delta = Object.fromEntries(
        Object.entries(grants).filter(([k, v]) => v !== !!previousData.grants?.[k]),
      ) as Employee["grants"];
      const serverClient = await getAuthenticatedServerClient();
      const updateDto = schemas.UpdateUserGrantsDto.parse({ delta: grantsToArray(delta) });
      await serverClient.updateGrants(updateDto, { params: { id } });
    }
    if (roles) {
      await setUserRoles(id, roles, entitledForDrivingSchools);
    }
    const personalDataUpdate = {
      ...(Object.hasOwn(data, "firstName") && firstName !== previousData.firstName ? { firstName } : {}),
      ...(Object.hasOwn(data, "lastName") && lastName !== previousData.lastName ? { lastName } : {}),
      ...(Object.hasOwn(data, "phoneNumber") && phoneNumber !== previousData.phoneNumber ? { phoneNumber } : {}),
    };
    if (Object.keys(personalDataUpdate).length > 0) {
      const serverClient = await getAuthenticatedServerClient();
      const updateDto = schemas.UpdateEmployeeDto.parse(personalDataUpdate);

      const expectedUpdateInFirestore = {
        ...(Object.hasOwn(personalDataUpdate, "firstName") || Object.hasOwn(personalDataUpdate, "lastName")
          ? {
              publicProfile: {
                ...(Object.hasOwn(personalDataUpdate, "firstName") ? { firstName: personalDataUpdate.firstName } : {}),
                ...(Object.hasOwn(personalDataUpdate, "lastName") ? { lastName: personalDataUpdate.lastName } : {}),
              },
            }
          : {}),
        ...(Object.hasOwn(personalDataUpdate, "phoneNumber") ? { phoneNumber: personalDataUpdate.phoneNumber } : {}),
      };

      const updateReceived = new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          unsubscribe();
          reject(new Error("Update timeout: changes did not appear in Firestore"));
        }, 10000);
        const unsubscribe = this.onUpdate(id, (snapshot) => {
          if (updateApplied(expectedUpdateInFirestore, snapshot.data())) {
            clearTimeout(timeoutId);
            unsubscribe();
            resolve();
          }
        });
      });

      await serverClient.updateEmployee(updateDto, { params: { id } });
      await updateReceived;
    }
    return this.getOne(resource, { id });
  }

  onUpdate(employeeId: string, listener: (snapshot: DocumentSnapshot) => void): () => void {
    return onSnapshot(doc(firestore, `users/${employeeId}`), listener);
  }
}

export const employeesProvider = new EmployeesProvider();

function _dtoToRecord(dto: EmployeeDto): Employee {
  const { firstName, lastName, roles } = dto;
  // driving school managers first, then instructors, then customer service (APP-5289) ...
  const roleSortOrder = ["drivingSchoolManager", "instructor", ...drivingSchoolEmployeeRoles];
  const sortOrderPrefix = roleSortOrder.reverse().reduce((acc, role, index) => {
    if (roles.includes(role)) {
      return roleSortOrder.length - index; // If record has more than one role, the earlier role in the roleSortOrder array, the higher sort order.
    }
    return acc;
  }, roleSortOrder.length + 1);

  // Role-based defaults (reflecting effective permissions from mapRolesToGrants) ...
  const grants: { [key: string]: boolean } = {
    ...getDefaultUserGrantToggles(
      roles as Role[],
      drivingSchoolsProvider.getOneFromCache(dto.drivingSchoolId!)!.customizations,
    ),
  };
  // Explicit Firestore grants override role-based defaults ...
  for (const grant of dto.grants ?? []) {
    if (grant.startsWith("!")) {
      grants[grant.substring(1)] = false;
    } else {
      grants[grant] = true;
    }
  }
  return {
    id: dto.firebaseAuthUserUid,
    drivingSchoolId: dto.drivingSchoolId,
    name: `${firstName} ${lastName}`.trim(),
    firstName,
    lastName,
    email: dto.email,
    phoneNumber: dto.phoneNumber,
    avatarUrl: `${serverBaseUrl}/user/${dto.firebaseAuthUserUid}/avatar`,
    disabled: dto.disabled,
    roles,
    entitledForDrivingSchools: dto.entitledForDrivingSchools,
    grants,
    _sortOrder: `${sortOrderPrefix} ${firstName} ${lastName}`,
  };
}
